import type { paths } from "../deferred-api/contracts";

export const importReviewPaths = {
  changes: "/v1/admin/partner-imports/batches/{batchId}/changes",
  issues: "/v1/admin/partner-imports/batches/{batchId}/issues",
  rows: "/v1/admin/partner-imports/batches/{batchId}/rows",
  aggregates: "/v1/admin/partner-imports/batches/{batchId}/aggregates",
  results: "/v1/admin/partner-imports/batches/{batchId}/results",
} as const;
export type ImportReviewKind = keyof typeof importReviewPaths;
export type ImportPageByKind = {
  [K in ImportReviewKind]: paths[(typeof importReviewPaths)[K]]["get"]["responses"][200]["content"]["application/json"];
};
export type ImportFiltersByKind = {
  [K in ImportReviewKind]: NonNullable<
    paths[(typeof importReviewPaths)[K]]["get"]["parameters"]["query"]
  >;
};
export type ImportStageInput = NonNullable<
  paths["/v1/admin/partner-imports/batches/{batchId}/stage"]["post"]["requestBody"]
>["content"]["application/json"];
export type ImportRegenerateInput = NonNullable<
  paths["/v1/admin/partner-imports/batches/{batchId}/regenerate"]["post"]["requestBody"]
>["content"]["application/json"];
export type ImportBatch =
  paths["/v1/admin/partner-imports/batches/{batchId}"]["get"]["responses"][200]["content"]["application/json"];
export type ImportPreviewInput = NonNullable<
  paths["/v1/admin/partner-imports/preview"]["post"]["requestBody"]
>["content"]["application/json"];
export type ImportReviewSummary =
  paths["/v1/admin/partner-imports/batches/{batchId}/review-summary"]["get"]["responses"][200]["content"]["application/json"];
export type ImportRowLocation =
  paths["/v1/admin/partner-imports/batches/{batchId}/locate"]["get"]["responses"][200]["content"]["application/json"];
export interface ImportCsvExport {
  csv: string;
  page: number;
  pageSize: number;
  total: number | null;
}
/** Transient operator input. Never put the reason in a URL, query key, or persisted cache. */
export interface ImportInvestigation {
  reason: string;
}
export type ImportReviewAccess = {
  investigation?: ImportInvestigation;
  cacheKey: string;
  signal: AbortSignal;
  assertCurrent: () => void;
};
export interface PartnerImportWorkflowRepository {
  listPartnerSetups(
    page: number,
    pageSize: number,
    signal?: AbortSignal,
  ): Promise<PartnerSetupPage>;
  getPartnerSetup(
    batchId: string,
    investigation?: ImportInvestigation,
    signal?: AbortSignal,
  ): Promise<PartnerSetup>;
  createPartnerSetup(input: PartnerSetupCreateInput): Promise<ImportBatch>;
  savePartnerSetup(batchId: string, input: PartnerSetupSaveInput): Promise<ImportBatch>;
  getPartnerImportSummary(batchId: string, signal?: AbortSignal): Promise<ImportReviewSummary>;
  locatePartnerImportRow(
    batchId: string,
    sourceRow: number,
    pageSize: number,
    signal?: AbortSignal,
  ): Promise<ImportRowLocation>;
  listPartnerImportReview<K extends ImportReviewKind>(
    batchId: string,
    kind: K,
    filters: ImportFiltersByKind[K],
    investigation?: ImportInvestigation,
    signal?: AbortSignal,
  ): Promise<ImportPageByKind[K]>;
  stagePartnerImport(batchId: string, input: ImportStageInput): Promise<ImportBatch>;
  regeneratePartnerImport(batchId: string, input: ImportRegenerateInput): Promise<ImportBatch>;
  exportPartnerImport<K extends "issues" | "results">(
    batchId: string,
    kind: K,
    filters: ImportFiltersByKind[K],
    investigation?: ImportInvestigation,
    signal?: AbortSignal,
  ): Promise<ImportCsvExport>;
}

export type PartnerSetup =
  paths["/v1/admin/partner-imports/setups/{batchId}"]["get"]["responses"][200]["content"]["application/json"];
export type PartnerSetupDraft = PartnerSetupCreateInput["draft"];
export type PartnerSetupPage =
  paths["/v1/admin/partner-imports/setups"]["get"]["responses"][200]["content"]["application/json"];
export type PartnerSetupCreateInput = NonNullable<
  paths["/v1/admin/partner-imports/setups"]["post"]["requestBody"]
>["content"]["application/json"];
export type PartnerSetupSaveInput = NonNullable<
  paths["/v1/admin/partner-imports/setups/{batchId}/save"]["post"]["requestBody"]
>["content"]["application/json"];
